import { useState } from "react";
import { Loader2, Sparkles } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, apiError } from "@/lib/api";

/** One click settles what the portal can work out on its own — after the officer sees it. */
export const SmartFixButton = ({ row, onDone }) => {
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const fills = row.smartfix || [];
  if (!fills.length) return null;

  const apply = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/inconsistency/${row.id}/smartfix`);
      toast.success(
        data.awaiting_verification
          ? `SmartFix for ${row.code} is with the unit SPOC to verify.`
          : `SmartFix settled ${data.fills.length} entr${data.fills.length === 1 ? "y" : "ies"} on ${row.code}.`,
      );
      setOpen(false);
      onDone();
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <button
        type="button"
        data-testid={`incon-smartfix-${row.code}`}
        onClick={() => setOpen(true)}
        title={`SmartFix can settle ${fills.length} entr${fills.length === 1 ? "y" : "ies"}`}
        className="inline-flex items-center gap-1.5 border border-primary bg-primary px-3 py-2 font-display text-[10px] font-bold uppercase tracking-widest text-primary-foreground transition-opacity duration-200 hover:opacity-85"
      >
        <Sparkles className="h-3 w-3" /> SmartFix
        <span className="font-data">{fills.length}</span>
      </button>
      <AlertDialog open={open} onOpenChange={(v) => !busy && setOpen(v)}>
        <AlertDialogContent className="max-w-xl rounded-none" data-testid="smartfix-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display">SmartFix {row.code}</AlertDialogTitle>
            <AlertDialogDescription>
              The portal works these out from the dates already on record. Each is marked as
              SmartFixed and stays the unit&apos;s to overwrite.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <ul className="max-h-[50vh] divide-y divide-border overflow-auto border border-border" data-testid="smartfix-fills">
            {fills.map((f) => (
              <li key={f.field} className="px-3 py-2 text-xs" data-testid={`smartfix-fill-${f.field}`}>
                <span className="font-semibold">{f.label}</span>
                <span className="font-data ml-2">{f.value}</span>
                <span className="block text-muted-foreground">{f.why}</span>
              </li>
            ))}
          </ul>
          <AlertDialogFooter>
            <AlertDialogCancel className="rounded-none" disabled={busy} data-testid="smartfix-cancel">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              className="rounded-none"
              disabled={busy}
              data-testid="smartfix-apply"
              onClick={(e) => {
                e.preventDefault();
                apply();
              }}
            >
              {busy ? <Loader2 className="mr-1.5 h-3.5 w-3.5 animate-spin" /> : <Sparkles className="mr-1.5 h-3.5 w-3.5" />}
              Apply SmartFix
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};
